import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type {
  QaRunsResponse,
  StartQaRunResponse,
  QaRunDetailResponse,
  QaRunReportsResponse,
  QaRunReportTimelineResponse,
  AttachPageToLatestRunResponse,
  ContinueQaReportResponse,
} from './qaRunTypes';

type PaginationOptions = {
  page?: number;
  pageSize?: number;
};

type AttachPageToLatestRunVariables = {
  canonicalPageId: string;
  credentialId?: string | null;
};

/**
 * Fetch all QA runs for the current organization (across all properties).
 */
export function useOrgQaRuns(options?: PaginationOptions) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;

  return useQuery({
    queryKey: ['qa-runs', 'org', page, pageSize],
    queryFn: async (): Promise<QaRunsResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });

      const response = await fetch(`${API_URL}/qa-runs?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch QA runs');
      }

      return response.json();
    },
  });
}

export function useQaRun(id: string | undefined) {
  return useQuery({
    queryKey: ['qa-run', id],
    queryFn: async (): Promise<QaRunDetailResponse> => {
      if (!id) {
        throw new Error('Missing QA run ID');
      }

      const response = await fetch(`${API_URL}/qa-runs/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch QA run');
      }

      return response.json();
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return status === 'running' || status === 'pending' ? 3000 : false;
    },
  });
}

export function useQaRunReports(runId: string | undefined, options?: PaginationOptions) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;

  return useQuery({
    queryKey: ['qa-run-reports', runId, page, pageSize],
    queryFn: async (): Promise<QaRunReportsResponse> => {
      if (!runId) {
        throw new Error('Missing QA run ID');
      }

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });

      const response = await fetch(`${API_URL}/qa-runs/${runId}/reports?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch reports');
      }

      return response.json();
    },
    enabled: !!runId,
  });
}

export function useQaRunReportTimeline(
  runId: string | undefined,
  options?: { bucketMinutes?: number; refetchInterval?: number | false }
) {
  const bucketMinutes = options?.bucketMinutes ?? 5;
  const refetchInterval = options?.refetchInterval ?? false;

  return useQuery({
    queryKey: ['qa-run-reports-timeline', runId, bucketMinutes],
    queryFn: async (): Promise<QaRunReportTimelineResponse> => {
      if (!runId) {
        throw new Error('Missing QA run ID');
      }

      const params = new URLSearchParams({
        bucketMinutes: String(bucketMinutes),
      });

      const response = await fetch(`${API_URL}/qa-runs/${runId}/reports/timeline?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch run timeline');
      }

      return response.json();
    },
    enabled: !!runId,
    refetchInterval,
  });
}

type StartQaRunVariables = {
  // Restrict to pages seen by these credentials (raw credential UUIDs).
  credentialIds?: string[];
};

export function useStartQaRun(propertyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: StartQaRunVariables): Promise<StartQaRunResponse> => {
      if (!propertyId) {
        throw new Error('Missing property ID');
      }

      const { credentialIds } = variables;
      const response = await fetch(`${API_URL}/properties/${propertyId}/qa-runs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(credentialIds && credentialIds.length > 0 ? { credentialIds } : {}),
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to start QA run');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-runs', propertyId] });
    },
  });
}

export function useAttachPageToLatestQaRun(propertyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      variables: AttachPageToLatestRunVariables
    ): Promise<AttachPageToLatestRunResponse> => {
      if (!propertyId) {
        throw new Error('Missing property ID');
      }

      const response = await fetch(
        `${API_URL}/properties/${propertyId}/canonical-pages/${variables.canonicalPageId}/qa-tests`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            credentialId: variables.credentialId ?? undefined,
            attachToLatestRun: true,
          }),
        }
      );

      const data = (await response.json().catch(() => ({}))) as Partial<
        AttachPageToLatestRunResponse & { error?: string }
      >;

      if (!response.ok || data.ok === false) {
        throw new Error(data.error || 'Failed to add QA test to latest run');
      }

      if (!data.qaRunId || !data.qaReportId || !data.jobId) {
        throw new Error('Unexpected response when adding QA test to latest run');
      }

      return {
        ok: true,
        qaRunId: data.qaRunId,
        qaReportId: data.qaReportId,
        jobId: data.jobId,
        brokerJobId: data.brokerJobId ?? null,
        replacedReportId: data.replacedReportId ?? null,
        createdNewRun: data.createdNewRun ?? false,
        attachedToLatestRun: data.attachedToLatestRun ?? true,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            (key[0] === 'qa-runs' ||
              key[0] === 'qa-run' ||
              key[0] === 'qa-run-reports' ||
              key[0] === 'canonical-page-detail' ||
              key[0] === 'canonical-page-qa-reports')
          );
        },
      });
    },
  });
}

export function useCancelQaRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/qa-runs/${id}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to cancel QA run');
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['qa-run', id] });
      queryClient.invalidateQueries({ queryKey: ['qa-runs'] });
    },
  });
}

type RerunPageTestResponse = {
  ok: boolean;
  newReportId: string;
  jobId: string;
};

export function useRerunPageTest(runId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: string): Promise<RerunPageTestResponse> => {
      if (!runId) {
        throw new Error('Missing run ID');
      }

      const response = await fetch(`${API_URL}/qa-runs/${runId}/reports/${reportId}/rerun`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to re-run page test');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate run details and reports
      queryClient.invalidateQueries({ queryKey: ['qa-run', runId] });
      queryClient.invalidateQueries({ queryKey: ['qa-run-reports', runId] });
      queryClient.invalidateQueries({ queryKey: ['qa-runs'] });
    },
  });
}

export function useDeleteQaRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/qa-runs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete QA run');
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['qa-run', id] });
      queryClient.invalidateQueries({ queryKey: ['qa-runs'] });
    },
  });
}

export function useContinueQaReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (qaReportId: string): Promise<ContinueQaReportResponse> => {
      if (!qaReportId) {
        throw new Error('Missing QA report ID');
      }

      const response = await fetch(`${API_URL}/qa/reports/${qaReportId}/continue`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = (await response.json().catch(() => ({}))) as Partial<ContinueQaReportResponse>;

      if (!response.ok || data.ok === false) {
        throw new Error(data.error || 'Failed to continue QA report');
      }

      if (!data.qaReportId || !data.jobId) {
        throw new Error('Unexpected response when continuing QA report');
      }

      return {
        ok: true,
        qaReportId: data.qaReportId,
        jobId: data.jobId,
        brokerJobId: data.brokerJobId ?? null,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            (key[0] === 'qa-run' ||
              key[0] === 'qa-runs' ||
              key[0] === 'qa-run-reports' ||
              key[0] === 'canonical-page-detail' ||
              key[0] === 'canonical-page-qa-reports')
          );
        },
      });
    },
  });
}
